import React, { useState, useEffect } from "react"
import AppBar from "@mui/material/AppBar"
import Box from "@mui/material/Box"
import Toolbar from "@mui/material/Toolbar"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import Menu from "@mui/material/Menu"
import Container from "@mui/material/Container"
import Avatar from "@mui/material/Avatar"
import Tooltip from "@mui/material/Tooltip"
import MenuItem from "@mui/material/MenuItem"
import Badge from "@mui/material/Badge"
import ListItemText from "@mui/material/ListItemText"
import ListItemAvatar from "@mui/material/ListItemAvatar"
import Button from "@mui/material/Button"
import Stack from "@mui/material/Stack"
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings"
import ForumIcon from "@mui/icons-material/Forum"
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone"
import TravelExploreIcon from "@mui/icons-material/TravelExplore"
import SettingsIcon from "@mui/icons-material/Settings"
import MenuBookIcon from "@mui/icons-material/MenuBook"
import FavoriteIcon from "@mui/icons-material/StarsRounded"
import ThumbDownIcon from "@mui/icons-material/ThumbDownRounded"
import LoginIcon from "@mui/icons-material/LoginRounded"
import LogoutIcon from "@mui/icons-material/LogoutRounded"
import { signIn, signOut, useSession } from "next-auth/react"
import { Alert, Divider, Snackbar, useMediaQuery } from "@mui/material"
import Image from "next/image"
import { useTranslation, useLanguage } from "@/contexts/LanguageContext"
import { useUserNotifications, UserNotification } from "@/hooks/useUserNotifications"
import { useRouter } from "next/router"
import { useUserPreferences } from "@/hooks/useUserPreferences"
import { HorizontalMenu } from "@/components/HorizontalMenu/HorizontalMenu"
import { formatter } from "@/utils/format"
import { isSessionAdmin } from "@/utils/auth"
import { UserAvatar } from "@/components/user/UserAvatar"

const capitalizeWords = (value: string | null | undefined): string =>
  (value ?? "").toLowerCase().replace(/(^|\s|-)\S/g, (match) => match.toUpperCase())

const Header = () => {
  const { data: session } = useSession()
  const { t } = useTranslation()
  const { language } = useLanguage()
  const locale = language === "pt" ? "pt-PT" : "en-US"
  const router = useRouter()
  const { preferences, refetch: refetchPreferences } = useUserPreferences()
  const isAdmin = isSessionAdmin(session)
  const menuLayout = preferences?.menuLayout || "side"
  const isDashboard = router.pathname === "/features" || router.asPath === "/#" || router.asPath === "/"

  // Listen for preferences updates
  useEffect(() => {
    const handlePreferencesUpdate = () => {
      refetchPreferences()
    }
    if (typeof window !== "undefined") {
      window.addEventListener("preferencesUpdated", handlePreferencesUpdate)
      return () => {
        window.removeEventListener("preferencesUpdated", handlePreferencesUpdate)
      }
    }
  }, [refetchPreferences])
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)
  const userEmail = session?.user?.email || null
  const {
    notifications,
    unreadCount: notificationsUnreadCount,
    markAsRead: markNotificationAsRead,
    markAllAsRead
  } = useUserNotifications(userEmail, { limit: 25 })
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null)
  const [chatNotificationSnackbarOpen, setChatNotificationSnackbarOpen] = useState(false)
  const [chatNotificationUnreadTotal, setChatNotificationUnreadTotal] = useState(0)
  // Tracks what the chat-unread-count logic below has already reacted to, across renders - a
  // `useState` (compared during render, "adjust state during render" style) rather than a login
  // effect + refs, since this repo's lint rules treat a `setState` call inside a `useEffect` body
  // as an error.
  const [chatNotificationTracking, setChatNotificationTracking] = useState<{
    userEmail: string | null
    initialized: boolean
    previousUnreadCount: number
  }>({ userEmail, initialized: false, previousUnreadCount: 0 })

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget)
  }

  const handleCloseUserMenu = () => {
    setAnchorElUser(null)
  }

  const handleOpenNotifications = (event: React.MouseEvent<HTMLElement>) => {
    if (!userEmail) return
    setNotificationAnchor(event.currentTarget)
  }

  const handleCloseNotifications = () => {
    setNotificationAnchor(null)
  }

  const handleMarkAllNotificationsRead = async () => {
    try {
      await markAllAsRead()
    } catch (error) {
      console.error("Failed to mark notifications as read", error)
    }
  }

  const formatNotificationTime = (notification: UserNotification) => {
    const ms =
      notification.createdAtMs ??
      (notification.createdAt && typeof notification.createdAt.toMillis === "function" ? notification.createdAt.toMillis() : null)
    return ms ? formatter.relativeDate(ms, t, { locale }) : ""
  }

  const getNotificationContent = (notification: UserNotification) => {
    const metadata = (notification.metadata ?? {}) as Record<string, unknown>
    if (notification.type === "chat") {
      const senderName = (metadata.senderName as string) || (metadata.senderId as string) || t("messages.drawer.unknownUser")
      const preview = (metadata.preview as string) || notification.body || ""
      return { title: t("notifications.types.chatTitle", { name: senderName }), description: preview, icon: <ForumIcon fontSize="small" /> }
    }
    if (notification.type === "property") {
      const propertyTitle = (metadata.propertyTitle as string) || notification.title || t("notifications.unknownProperty")
      const filters = Array.isArray(metadata.filters) ? (metadata.filters as string[]) : []
      return {
        title: t("notifications.types.propertyTitle", { title: propertyTitle }),
        description:
          filters.length > 0
            ? t("notifications.types.propertyDescriptionFiltered", { filters: filters.join(", ") })
            : t("notifications.types.propertyDescription"),
        icon: <TravelExploreIcon fontSize="small" />
      }
    }
    return {
      title: notification.title || t("notifications.title"),
      description: notification.body || "",
      icon: <NotificationsNoneIcon fontSize="small" />
    }
  }

  const handleNotificationSelect = async (notification: UserNotification) => {
    try {
      await markNotificationAsRead(notification.id)
    } catch (error) {
      console.error("Failed to mark notification as read", error)
    }
    setNotificationAnchor(null)

    if (notification.type === "chat") {
      const metadata = (notification.metadata ?? {}) as Record<string, unknown>
      const threadId = metadata.threadId as string | undefined
      if (threadId) {
        router.push(`/features/chat?threadId=${encodeURIComponent(threadId)}`)
      } else {
        router.push("/features/chat")
      }
      return
    }

    if (notification.type === "property") {
      const metadata = (notification.metadata ?? {}) as Record<string, unknown>
      const propertyId = metadata.propertyId as string | undefined
      if (propertyId) {
        router.push(`/features/search/${encodeURIComponent(propertyId)}`)
      }
    }
  }

  const notificationMenuOpen = Boolean(notificationAnchor)

  const tabletCheck = useMediaQuery("(min-width: 768px)")

  // Adjust state during render (this repo's lint-enforced alternative to a `useEffect` that just
  // reacts to a changed value - see the matching pattern elsewhere in the codebase) rather than
  // two separate effects: a login/logout transition resets the tracking baseline (and, on logout,
  // the visible notification state); otherwise, once logged in, a change in the chat-unread count
  // updates the badge and pops the snackbar on an increase, same as the effects this replaced.
  if (userEmail !== chatNotificationTracking.userEmail) {
    setChatNotificationTracking({ userEmail, initialized: false, previousUnreadCount: 0 })
    if (!userEmail) {
      setNotificationAnchor(null)
      setChatNotificationSnackbarOpen(false)
      setChatNotificationUnreadTotal(0)
    }
  } else if (userEmail) {
    const unreadChatCount = notifications.filter((notification) => !notification.read && notification.type === "chat").length

    if (!chatNotificationTracking.initialized) {
      setChatNotificationTracking({ userEmail, initialized: true, previousUnreadCount: unreadChatCount })
      setChatNotificationUnreadTotal(unreadChatCount)
      if (unreadChatCount > 0) setChatNotificationSnackbarOpen(true)
    } else if (unreadChatCount !== chatNotificationTracking.previousUnreadCount) {
      setChatNotificationTracking({ userEmail, initialized: true, previousUnreadCount: unreadChatCount })
      if (unreadChatCount > chatNotificationTracking.previousUnreadCount) {
        setChatNotificationUnreadTotal(unreadChatCount)
        setChatNotificationSnackbarOpen(true)
      } else if (unreadChatCount === 0) {
        setChatNotificationUnreadTotal(0)
        setChatNotificationSnackbarOpen(false)
      } else {
        setChatNotificationUnreadTotal(unreadChatCount)
      }
    }
  }

  const handleCloseChatNotificationSnackbar = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return
    }
    setChatNotificationSnackbarOpen(false)
  }

  const renderNotificationButton = () => (
    <Tooltip title={t("notifications.title")}>
      <span>
        <IconButton onClick={handleOpenNotifications} color="inherit" disabled={!userEmail} sx={{ p: 0.5 }}>
          <Badge color="error" badgeContent={notificationsUnreadCount} invisible={notificationsUnreadCount === 0}>
            <NotificationsNoneIcon fontSize="small" />
          </Badge>
        </IconButton>
      </span>
    </Tooltip>
  )

  return (
    <>
      <AppBar
        position="fixed" // Set position to fixed to make it stay at the top
        sx={{
          width: "100%",
          boxShadow: "none",
          zIndex: 1201, // Ensure it's on top of other components
          minHeight: menuLayout === "horizontal" ? "69px" : "64px" // 5px more height when horizontal menu
          //backgroundColor: "rgba(255, 255, 255, 0.4)", // Softer white with transparency
          //backdropFilter: "blur(20px)", // Stronger blur for a premium effect
          //border: "1px solid rgba(255, 255, 255, 0.6)", // Soft white border
        }}>
        <Container maxWidth={false}>
          <Toolbar disableGutters sx={{ position: "relative" }}>
            <Box
              component="button"
              onClick={() => router.push("/features")}
              sx={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                zIndex: 1,
                "&:hover": { opacity: 0.8 },
                transition: "opacity 0.2s ease"
              }}
              aria-label="Go to Dashboard">
              <Image src="/media/images/odynxLogo.png" alt="Odynx Logo" width={99} height={25} priority />
            </Box>

            {/* Horizontal Menu - centered when menuLayout is 'horizontal' and not on dashboard - hidden on mobile */}
            {menuLayout === "horizontal" && session && !isDashboard && (
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 1,
                  display: { xs: "none", md: "block" } // Hide on mobile, show on desktop
                }}>
                <HorizontalMenu />
              </Box>
            )}

            {tabletCheck && (
              <Box sx={{ paddingRight: 1, marginLeft: "auto", display: "flex", alignItems: "center", gap: 1, zIndex: 1 }}>
                {renderNotificationButton()}
                {isAdmin && (
                  <Tooltip title={t("header.adminUserTooltip")}>
                    <AdminPanelSettingsIcon color="primary" fontSize="small" />
                  </Tooltip>
                )}
              </Box>
            )}

            {/* `ml: auto` only below the tablet breakpoint: the notification/admin box above (the one
                that pushes this block to the right) isn't rendered there. Keeping it on tablet+ too
                splits the free space between both boxes and strands that box mid-header. */}
            <Box
              sx={{ flexGrow: 0, display: "flex", alignItems: "center", gap: 0.75, pr: tabletCheck ? 2 : 0, ml: tabletCheck ? 0 : "auto" }}>
              {!tabletCheck && <>{renderNotificationButton()}</>}
              <Tooltip title={t("header.profileTooltip")}>
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <UserAvatar name={session?.user?.name} email={session?.user?.email} size={36} />
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                keepMounted
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}>
                {session ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, px: 2, py: 1 }}>
                    <UserAvatar name={session.user?.name} email={session.user?.email} size={34} />
                    <Box sx={{ minWidth: 0, maxWidth: { xs: "60vw", sm: 280 } }}>
                      <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                        {capitalizeWords(session.user?.name)}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary" noWrap sx={{ fontWeight: 400 }}>
                        {session.user?.email}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <MenuItem disabled>
                    <Typography textAlign="center">{t("header.guest")}</Typography>
                  </MenuItem>
                )}
                {session && (
                  <MenuItem
                    disabled
                    onClick={() => {
                      handleCloseUserMenu()
                      router.push("/features/manual")
                    }}>
                    <MenuBookIcon sx={{ mr: 1, fontSize: 20 }} />
                    <Typography textAlign="center">{t("sideMenu.userManual")}</Typography>
                  </MenuItem>
                )}
                {session && (
                  <MenuItem
                    onClick={() => {
                      handleCloseUserMenu()
                      router.push("/features/user-settings")
                    }}>
                    <SettingsIcon sx={{ mr: 1, fontSize: 20 }} />
                    <Typography textAlign="center">{t("header.userSettings")}</Typography>
                  </MenuItem>
                )}
                {session && <Divider />}
                {session &&
                  [
                    { href: "/features/search/favorites", label: t("sideMenu.favorites"), Icon: FavoriteIcon },
                    { href: "/features/search/disliked", label: t("sideMenu.disliked"), Icon: ThumbDownIcon }
                  ].map(({ href, label, Icon }) => (
                    <MenuItem
                      key={href}
                      onClick={() => {
                        handleCloseUserMenu()
                        router.push(href)
                      }}>
                      <Icon sx={{ mr: 1, fontSize: 20 }} />
                      <Typography textAlign="center">{label}</Typography>
                    </MenuItem>
                  ))}
                <Divider />
                <MenuItem onClick={() => (session ? signOut() : signIn())}>
                  {session ? (
                    <LogoutIcon sx={{ mr: 1, fontSize: 20, stroke: "currentColor", strokeWidth: 1 }} />
                  ) : (
                    <LoginIcon sx={{ mr: 1, fontSize: 20, stroke: "currentColor", strokeWidth: 1 }} />
                  )}
                  <Typography textAlign="center">{session ? t("header.logout") : t("header.login")}</Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
        <Divider />
      </AppBar>
      <Menu
        anchorEl={notificationAnchor}
        open={notificationMenuOpen}
        onClose={handleCloseNotifications}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 360, maxWidth: "90vw" } }}>
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1">{t("notifications.title")}</Typography>
            {notificationsUnreadCount > 0 && (
              <Button
                size="small"
                onClick={(event) => {
                  event.stopPropagation()
                  handleMarkAllNotificationsRead()
                }}>
                {t("notifications.markAllRead")}
              </Button>
            )}
          </Stack>
        </Box>
        {(() => {
          const unreadNotifications = notifications.filter((notification) => !notification.read)
          const hasNotifications = unreadNotifications.length > 0

          return !hasNotifications ? (
            <MenuItem disabled>
              <ListItemText primary={t("notifications.empty")} />
            </MenuItem>
          ) : (
            unreadNotifications.map((notification) => {
              const content = getNotificationContent(notification)
              const timeLabel = formatNotificationTime(notification)
              return (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationSelect(notification)}
                  sx={{ alignItems: "flex-start", gap: 1 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "primary.light", color: "primary.contrastText", width: 32, height: 32 }}>{content.icon}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{ fontWeight: 600, variant: "body1" }}
                    secondaryTypographyProps={{ component: "div" }}
                    primary={content.title}
                    secondary={
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                        {content.description && (
                          <Typography variant="body2" color="text.secondary">
                            {content.description}
                          </Typography>
                        )}
                        {timeLabel && (
                          <Typography variant="caption" color="text.secondary">
                            {timeLabel}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </MenuItem>
              )
            })
          )
        })()}
      </Menu>
      <Snackbar
        open={chatNotificationSnackbarOpen && chatNotificationUnreadTotal > 0}
        autoHideDuration={6000}
        onClose={handleCloseChatNotificationSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        // Lift above the fixed mobile bottom nav (64px) so the toast isn't hidden behind it.
        sx={{ bottom: { xs: 80, md: 24 } }}>
        <Alert onClose={handleCloseChatNotificationSnackbar} severity="info" sx={{ width: "100%" }}>
          {chatNotificationUnreadTotal === 1
            ? t("notifications.chatUnreadSingular")
            : t("notifications.chatUnreadPlural", { count: chatNotificationUnreadTotal })}
        </Alert>
      </Snackbar>
    </>
  )
}
export default Header
